import { TCertificateFilters } from '../components/organisms/CertificateFilter/CertificateFiltersModal';
import { TIsolationFilters } from '../components/organisms/IsolationFilter/IsolationFiltersModal';
import { TJobFilters } from '../components/organisms/JobFiltersModal/JobFiltersModal';
import { TLeakTestPackFilters } from '../components/organisms/LeakTestPackFiltersModal/LeakTestPackFiltersModal';
import { TPermitFilters } from '../components/organisms/PermitFiltersModal/PermitFiltersModal';
import {
  CertificateStatusEnum,
  GocStatusEnum,
  JobFieldWalkStatusEnum,
  JobScheduleStatusEnum,
  PermitRequirementEnum,
  PermitWalkedEnum,
} from '../graph-queries/__generated__/graphql';
import { CERTIFICATE_STATUS_ENUM } from './constants/dbConstants';
import {
  TEmoc,
  TEvent,
  TInsulation,
  TIsolation,
  TJob,
  TLeakTestPack,
  TLiftPlan,
  TPermit,
  TScaffold,
  TWorkpack,
  TProcedure,
  TPurchaseOrder,
  TGoc,
  TCertificate,
  TJoint,
} from './types';

export const EMPTY_JOB: TJob = {
  jobId: -1,
  eventId: -1,
  jobDescription: '',
  scheduleStatus: JobScheduleStatusEnum.L2,
  fieldWalk: JobFieldWalkStatusEnum.Required,
  moNumber: '',
  functionalLocation: '',
  assignedPlanner: '',
  notes: '',
  updatedAt: -1,
  updatedBy: '',
  workpackRequired: null,
  mocRequired: null,
  materialsRequired: null,
  longLeadMaterialsRequired: null,
  specialistVendorsRequired: null,
  specialistToolingRequired: null,
  scaffoldRequired: null,
  insulationRequired: null,
  liftPlanRequired: null,
  ropeAccessRequired: null,
  isolationRequired: null,
  riggingRequired: null,
  heatTracingRequired: null,
};

export const EMPTY_EVENT: TEvent = {
  eventId: -1,
  assetId: -1,
  eventDescription: '',
  revisionCode: '',
  eventStartDate: -1,
  eventEndDate: -1,
  eventScopeFreezeDate: -1,
  updatedAt: -1,
  updatedBy: '',
};

// Workpacks

export const EMPTY_WORKPACK: Omit<TWorkpack, 'type' | 'status'> = {
  workpackId: -1,
  assetId: -1,
  workpackNumber: '',
  vendorJobNumber: '',
  vendor: '',
  title: '',
  sorNumber: '',
  updatedAt: -1,
  updatedBy: '',
  comments: '',
};

// Scaffolds

export const EMPTY_SCAFFOLD: Omit<TScaffold, 'status'> = {
  scaffoldId: -1,
  assetId: -1,
  planRef: '',
  description: '',
  weight: -1,
  safeWorkingLimit: '',
  width: -1,
  height: -1,
  length: -1,
  teamSize: '',
  erectHours: -1,
  dismantleHours: -1,
  outsideCover: false,
  comments: '',
  updatedAt: -1,
  updatedBy: '',
  tarJobs: [],
};

// Liftplans
export const EMPTY_LIFTPLAN: Omit<
  TLiftPlan,
  'type' | 'status' | 'equipmentStatus' | 'structuralAssessment'
> = {
  liftPlanId: -1,
  assetId: -1,
  planNumber: '',
  description: '',
  safeWorking: '',
  erectHours: -1,
  deconstructHours: -1,
  oversideCover: false,
  comments: '',
  updatedAt: -1,
  updatedBy: '',
};

// eMocs
export const EMPTY_EMOC: TEmoc = {
  assetId: -1,
  mocNumber: '',
  mocId: -1,
  comments: '',
  updatedAt: -1,
  updatedBy: '',
};

// Goc
export const EMPTY_GOC: TGoc = {
  jobId: -1,
  sh1: '',
  mc1: '',
  status: GocStatusEnum.Required,
  comments: '',
  updatedAt: -1,
  updatedBy: '',
};

// Others
export const EMPTY_ISOLATION: Omit<TIsolation, 'drawingStatus' | 'source' | 'lineWalkPackStatus'> =
  {
    loshNumber: '',
    fieldWalked: false,
    isolationId: -1,
    comments: '',
    updatedAt: -1,
    updatedBy: '',
    title: '',
    isolationNumber: '',
    requiredTrades: '',
    totalHours: -1,
    packNumber: -1,
    assetId: -1,
  };

export const EMPTY_PERMIT: TPermit = {
  taskId: -1,
  permitNumber: '',
  permitOwner: '',
  moNumber: '',
  permitRequirement: PermitRequirementEnum.Required,
  comments: '',
  permitWalked: PermitWalkedEnum.Required,
  lastKnownTaskDescription: '',
};

export const EMPTY_INSULATION: Omit<TInsulation, 'status' | 'materials' | 'heatTracing'> = {
  insulationId: -1,
  comments: '',
  updatedAt: -1,
  updatedBy: '',
  referenceNumber: '',
  description: '',
  circuitNumber: '',
  assetId: -1,
};

// LeakTestPacks
export const EMPTY_LEAK_TEST_PACK: Omit<
  TLeakTestPack,
  'leakTestPackType' | 'opsReview' | 'engineeringReview' | 'assetReview' | 'status'
> = {
  leakTestPackId: -1,
  assetId: -1,
  leakTestPackTitle: '',
  trackPackNumber: '',
  systemNumber: '',
  spa: '',
  comments: '',
  updatedAt: -1,
  updatedBy: '',
};

// Purchase Orders
export const EMPTY_PURCHASE_ORDER: Omit<TPurchaseOrder, 'status' | 'type'> = {
  purchaseOrderId: -1,
  poNumber: '',
  assetId: -1,
  vendor: '',
  comments: '',
  updatedAt: -1,
  updatedBy: '',
};

// Procedures
export const EMPTY_PROCEDURE: Omit<TProcedure, 'category' | 'status'> = {
  procedureId: -1,
  procedureNumber: '',
  title: '',
  url: '',
  comments: '',
  assetId: -1,
  updatedAt: -1,
  updatedBy: '',
};

// Job filters
export const EMPTY_JOB_FILTERS: TJobFilters = {
  workpackStatus: [],
  scaffoldStatus: [],
  gocStatus: [],
  scheduleStatus: [],
  insulationStatus: [],
  procedureStatus: [],
  leakTestPackStatus: [],
  liftPlanStatus: [],
  fieldWalkStatus: [],
  purchaseOrderStatus: [],
  assignedPlanner: [],
  jobId: [],
  permitStatus: [],
  gocSh1: [],
  isolationStatus: [],
  mocStatus: [],
};

// Permit filters
export const EMPTY_PERMIT_FILTERS: TPermitFilters = {
  'ecowPermit.statusComment': [],
  taskWorkCenter: [],
  gocSh1: [],
  permitOwner: [],
  event: [],
};

// Isolation filters
export const EMPTY_ISOLATION_FILTERS: TIsolationFilters = {
  'ecowIsolation.statusComment': [],
  event: [],
  loshNumber: [],
  requiredTrades: [],
  isolationNumber: [],
  fieldWalked: [],
  drawingStatus: [],
};

// Leak test filters
export const EMPTY_LEAK_TEST_PACK_FILTERS: TLeakTestPackFilters = {
  event: [],
  systemNumber: [],
  trackPackNumber: [],
  leakTestPackTitle: [],
  leakTestPackType: [],
};

// Certificate filters
export const INITIAL_CERTIFICATE_FILTERS: TCertificateFilters = {
  isolationNumber: [],
  maintenanceOrder: [],
  mc1: [],
  sh1: [],
  permitNumber: [],
  status: [
    CERTIFICATE_STATUS_ENUM[CertificateStatusEnum.Draft],
    CERTIFICATE_STATUS_ENUM[CertificateStatusEnum.IssuedForReview],
  ],
  system: [],
  template: [],
  area: [],
  completedStep: [],
};

export const MONITOR_CERTIFICATE_FILTERS: TCertificateFilters = {
  isolationNumber: [],
  maintenanceOrder: [],
  mc1: [],
  sh1: [],
  permitNumber: [],
  status: [],
  system: [],
  template: [],
  area: [],
  completedStep: [],
};

// Certificates
export const EMPTY_CERTIFICATE: TCertificate = {
  certificateId: -1,
  assetId: -1,
  template: false,
  title: '',
  certificateNumber: '',
  status: '',
  type: '',
  maintenanceOrder: '',
  permitNumber: '',
  isolationNumber: '',
};

// Joints
export const EMPTY_JOINT: TJoint = {
  jointId: -1,
  certificateId: -1,
  jointNumber: '',
  additionalDetails: '',
  tagId: '',
  pIdNo: '',
  flangeSize: '',
  rating: '',
  boltDiameter: '',
  boltQuantity: '',
  boltLength: '',
  boltMaterial: '',
  gasketType: '',
  torqueValue: '',
  hydraulicToolRef: '',
  hydraulicToolPass: '',
  hydraulicToolPumpPressure: '',
  updatedAt: -1,
  updatedBy: '',
  hasFlangeDefect: false,
  actualTorque: '',
  supervisedAssembly: false,
};
